import { AsyncMultiSelect } from "@src/components/elements/Select/AsyncMultiSelect";
import { SelectOption } from "@src/components/elements/Select/SelectOption";
import { sudoziFetch } from "@src/infrastructure/apiCall";
import {
  GetMemberSelectOptionsRoute,
  GetMembersRoute,
} from "@src/sudozi-schemas/api-route-schemas";
import { userName } from "@src/utils/userName";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { MultiValue } from "react-select";

type Props = {
  label: string;
  disableError?: boolean;
  value: string[];
  onChange: (values: MultiValue<SelectOption>) => void;
  menuPortalTarget?: HTMLElement;
  placeholder?: string;
  error?: string;
  onCreateOption?: (value: string) => Promise<SelectOption | undefined>;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  size?: "small" | "large";
};

export const AsyncMembersSelect: React.FC<Props> = ({
  label,
  value,
  onChange,
  disableError,
  menuPortalTarget,
  placeholder,
  error,
  onCreateOption,
  disabled,
  required,
  className,
  size,
}) => {
  async function loadOptions(
    search: string | null,
    userIdsToInclude?: string[],
  ) {
    const options: SelectOption[][] = await Promise.all([
      await sudoziFetch({
        route: GetMemberSelectOptionsRoute,
        query: { search },
      }),
      !!userIdsToInclude
        ? await sudoziFetch({
            route: GetMembersRoute,
            query: { userIds: userIdsToInclude },
          }).then((users) =>
            users.edges.map((edge) => ({
              label: userName(edge.node),
              value: edge.node.id,
            })),
          )
        : [],
    ]);

    return options.flat();
  }

  const [defaultOptions, setDefaultOptions] = useState<SelectOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<SelectOption[]>([]);

  const options = _.uniqBy(defaultOptions.concat(selectedOptions), "value");
  const internalValue = options.filter((o) => value.includes(o.value));

  async function fetchDefaultOptions() {
    const opts = await loadOptions(null, value);
    void setDefaultOptions(opts);
  }

  useEffect(() => {
    void fetchDefaultOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return defaultOptions ? (
    <AsyncMultiSelect
      size={size}
      className={className}
      required={required}
      minWidth="100%"
      error={error}
      isDisabled={disabled}
      defaultOptions={options}
      disableError={disableError}
      label={label}
      isCreatable={!!onCreateOption}
      onCreateOption={async (value: string) => {
        if (!onCreateOption) return;

        const option = await onCreateOption(value);

        if (!option) return;

        setSelectedOptions([...selectedOptions, option]);
      }}
      value={internalValue}
      loadOptions={loadOptions}
      placeholder={placeholder ?? "Search for a member"}
      onChange={(values) => {
        setSelectedOptions([...values]);
        onChange(values);
      }}
      menuPortalTarget={menuPortalTarget}
    />
  ) : (
    <div>Loading...</div>
  );
};
